import {Divider, Stack, Table, TableBody, TableCell, TableRow, Typography, tableCellClasses} from "@mui/material";
import {TooltipProps} from "recharts/types/component/Tooltip";
import dayjs from "dayjs";

export const CHART_ITEM_HIDDEN_LABEL = "CHART_ITEM_HIDDEN";
export type ChartTooltipValueFormatter = (value: number) => string | number;

interface BaseChartTooltipProps extends TooltipProps<number, string> {
    valueFormatter?: ChartTooltipValueFormatter;
}

export const BaseChartTooltip: React.FC<BaseChartTooltipProps> = ({active, payload, label, valueFormatter}) => {
    if (active && payload?.length) {
        return (
            <Stack
                direction="column"
                padding="1rem"
                bgcolor="#FAFCFF"
                border={1}
                borderColor="#E9EBF1 !important"
                sx={{opacity: 0.9}}
            >
                <Typography>{formatTooltipLabel(label as Date)}</Typography>
                <Divider sx={{marginTop: "0.2rem", marginBottom: "1rem"}} />
                <Table size="small">
                    <TableBody
                        sx={{
                            [`& .${tableCellClasses.root}`]: {
                                borderBottom: "none",
                                paddingY: 0,
                                color: "#545A6E",
                                fontSize: 14,
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                            },
                        }}
                    >
                        {payload
                            .filter((item) => item.name !== CHART_ITEM_HIDDEN_LABEL)
                            .map((item) => {
                                const value = getTooltipValue(item.value as unknown);
                                const renderedValue =
                                    typeof value === "number" && valueFormatter ? valueFormatter(value) : value;

                                return (
                                    <TableRow key={item.dataKey}>
                                        <TableCell sx={{paddingLeft: 0}}>
                                            <Typography color={item.color}>
                                                {item.name}
                                                {item.unit && ` (${item.unit as string})`}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{paddingRight: 0}}>
                                            <Typography>{renderedValue}</Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </Stack>
        );
    }

    return null;
};

const formatTooltipLabel = (tick: Date) => {
    return dayjs(tick).format("dddd, MMM D, YYYY").toUpperCase();
};

const getTooltipValue = (value: unknown): number | string | undefined => {
    if (!Array.isArray(value)) {
        return typeof value === "number" || typeof value === "string" ? value : undefined;
    }

    const valueArray = value as unknown[];
    const last = valueArray[valueArray.length - 1];
    return typeof last === "number" || typeof last === "string" ? last : undefined;
};
